/**
 * createJobsApp — the single Vue app factory for every page-level mount.
 *
 * The Jobs app uses ONE Vue island per page (jobs, jobdetail, apply, postjob,
 * myapplications). Each island's template is inline inside a Razor .cshtml file
 * and compiled at runtime by Vue (webpack alias: vue/dist/vue.esm-bundler.js).
 *
 * Because templates live in Razor, the Shadcn-Vue SFCs must be globally
 * registered so `<Button>`, `<Card>`, `<Select>` etc. resolve at runtime.
 * That's what this factory does.
 *
 * Pages call:
 *   const app = createJobsApp({ data() {...}, methods: {...} });
 *   app.mount('#jobs-app');
 *
 * Add a new Shadcn component:
 *   1. `npx shadcn-vue@latest add <component>` — drops SFC in components/ui/<name>/
 *   2. Import + registerGroup() it below.
 */

import { createApp, type App, type Component } from "vue";

// Shadcn-Vue primitives
import {
    Alert, AlertDescription, AlertTitle,
} from "@/components/ui/alert";
import {
    Avatar, AvatarFallback, AvatarImage,
} from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Card, CardAction, CardContent, CardDescription,
    CardFooter, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogClose, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogOverlay, DialogScrollContent,
    DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
    DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator,
    DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent,
    DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover, PopoverAnchor, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
    Select, SelectContent, SelectGroup, SelectItem, SelectItemText,
    SelectLabel, SelectScrollDownButton, SelectScrollUpButton,
    SelectSeparator, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
    Sheet, SheetClose, SheetContent, SheetDescription,
    SheetFooter, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

// Reka UI Collapsible primitive — used for the job-cluster expand/collapse
// animation on /jobs. Native data-state + --reka-collapsible-content-height
// custom property gives us the "animate to height: auto" behavior without
// measuring in JS.
import {
    CollapsibleRoot, CollapsibleContent,
} from "reka-ui";

// Global icon wrapper backed by lucide-vue-next
import Icon from "@/components/Icon.vue";

// Exported so prerender-entry.ts can register the identical globals on the
// SSR app (createSSRApp) used by the build-time /employer prerender.
export const globalComponents: Record<string, Component> = {
    // Alerts
    Alert, AlertDescription, AlertTitle,

    // Avatar
    Avatar, AvatarFallback, AvatarImage,

    // Badge
    Badge,

    // Button
    Button,

    // Card
    Card, CardAction, CardContent, CardDescription,
    CardFooter, CardHeader, CardTitle,

    // Checkbox
    Checkbox,

    // Dialog
    Dialog, DialogClose, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogOverlay, DialogScrollContent,
    DialogTitle, DialogTrigger,

    // Dropdown menu
    DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
    DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator,
    DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent,
    DropdownMenuSubTrigger, DropdownMenuTrigger,

    // Input / form
    Input, Label, Textarea,

    // Popover
    Popover, PopoverAnchor, PopoverContent, PopoverTrigger,

    // Radio
    RadioGroup, RadioGroupItem,

    // Scroll area
    ScrollArea, ScrollBar,

    // Select
    Select, SelectContent, SelectGroup, SelectItem, SelectItemText,
    SelectLabel, SelectScrollDownButton, SelectScrollUpButton,
    SelectSeparator, SelectTrigger, SelectValue,

    // Separator
    Separator,

    // Sheet
    Sheet, SheetClose, SheetContent, SheetDescription,
    SheetFooter, SheetHeader, SheetTitle, SheetTrigger,

    // Skeleton
    Skeleton,

    // Tooltip
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,

    // Reka UI Collapsible (used by JobsPage cluster expand/collapse)
    CollapsibleRoot, CollapsibleContent,

    // Icon wrapper (Lucide)
    Icon,
};

/**
 * Create a Vue app with every Shadcn-Vue component + <Icon> pre-registered.
 * Drop-in replacement for `createApp(...)`.
 * Employer components are NOT registered here — they're imported locally
 * by EmployerLayout.vue and lazy-loaded per route for code splitting.
 */
export function createJobsApp(rootComponent: Component | Record<string, any>): App {
    const app = createApp(rootComponent as Component);
    for (const [name, component] of Object.entries(globalComponents)) {
        app.component(name, component);
    }
    return app;
}
